/**
 * Role-Based Navigation Configuration
 * 
 * Single source of truth for navigation items and their role requirements.
 * 
 * Design:
 * - User role items: displayed flat (not in collapsible group)
 * - Manager role items: displayed in collapsible "Manager" group  
 * - Admin role items: displayed in collapsible "Admin" group
 * 
 * To add new roles:
 * 1. Add role to RoleRegistry in backend (app/lib/role_registry.rb)
 * 2. Add role level mapping here
 * 3. Add navigation items with appropriate role assignment
 */

import { 
  Home, 
  User, 
  Settings, 
  LayoutDashboard, 
  MessageSquare, 
  Users, 
  Link, 
  Shield,
  FileText,
  Flag,
  FileEdit,
  FolderOpen,
  Tag,
  Mail,
  Image,
  BarChart3
} from 'lucide-react';

// Role type - matches backend RoleRegistry
export type Role = 'user' | 'manager' | 'admin';

// Role level mapping - must match backend RoleRegistry
export const ROLE_LEVELS: Record<Role, number> = {
  user: 0,
  manager: 1,
  admin: 2,
};

// Navigation item interface
export interface NavItem {
  id: string;                    // Unique identifier
  href: string;                   // Route path
  label: string;                  // Display label
  icon?: React.ComponentType;     // Icon component
  requiredRole: Role;            // Minimum role required
  requiredLevel: number;         // Minimum level (0, 1, 2)
  group?: string;                 // Group identifier (for collapsible)
}

// Group configuration
export interface NavGroup {
  id: string;
  label: string;
  icon?: React.ComponentType;
  minLevel: number;               // Minimum level to show this group
  defaultOpen?: boolean;         // Whether group is expanded by default
}

// Define all navigation groups
export const NAV_GROUPS: NavGroup[] = [
  {
    id: 'user',
    label: '', // Empty label = no group header for user items
    minLevel: 0,
  },
  {
    id: 'manager',
    label: 'Manager',
    icon: Users,
    minLevel: 1,
    defaultOpen: false,
  },
  {
    id: 'admin',
    label: 'Admin',
    icon: Shield,
    minLevel: 2,
    defaultOpen: false,
  },
];

// Define all navigation items
export const NAVIGATION_ITEMS: NavItem[] = [
  // ===== USER LEVEL (Level 0) - Shown flat, no group =====
  {
    id: 'home',
    href: '/dashboard',
    label: 'Home',
    icon: Home,
    requiredRole: 'user',
    requiredLevel: 0,
    group: 'user',
  },
  {
    id: 'profile',
    href: '/users/profile',
    label: 'Profile',
    icon: User,
    requiredRole: 'user',
    requiredLevel: 0,
    group: 'user',
  },
  {
    id: 'settings',
    href: '/users/settings',
    label: 'Settings',
    icon: Settings,
    requiredRole: 'user',
    requiredLevel: 0,
    group: 'user',
  },

  // ===== MANAGER LEVEL (Level 1) - Collapsible Manager group =====
  {
    id: 'chat',
    href: '/chat-dashboard',
    label: 'Chat',
    icon: MessageSquare,
    requiredRole: 'manager',
    requiredLevel: 1,
    group: 'manager',
  },
  {
    id: 'assets',
    href: '/admin/assets',
    label: 'Assets',
    icon: Image,
    requiredRole: 'manager',
    requiredLevel: 1,
    group: 'manager',
  },
  {
    id: 'users_list',
    href: '/admin/users',
    label: 'Users',
    icon: Users,
    requiredRole: 'manager',
    requiredLevel: 1,
    group: 'manager',
  },
  {
    id: 'blog_posts',
    href: '/admin/blog/posts',
    label: 'Blog Posts',
    icon: FileEdit,
    requiredRole: 'manager',
    requiredLevel: 1,
    group: 'manager',
  },
  {
    id: 'blog_categories',
    href: '/admin/blog/categories',
    label: 'Blog Categories',
    icon: FolderOpen,
    requiredRole: 'manager',
    requiredLevel: 1,
    group: 'manager',
  },
  {
    id: 'blog_tags',
    href: '/admin/blog/tags',
    label: 'Blog Tags',
    icon: Tag,
    requiredRole: 'manager',
    requiredLevel: 1,
    group: 'manager',
  },
  {
    id: 'newsletters',
    href: '/admin/newsletters',
    label: 'Newsletters',
    icon: Mail,
    requiredRole: 'manager',
    requiredLevel: 1,
    group: 'manager',
  },

  // ===== ADMIN LEVEL (Level 2) - Collapsible Admin group =====
  {
    id: 'admin_panel',
    href: '/admin',
    label: 'Admin Panel',
    icon: Shield,
    requiredRole: 'admin',
    requiredLevel: 2,
    group: 'admin',
  },
  {
    id: 'legal_documents',
    href: '/admin/legal_documents',
    label: 'Legal Documents',
    icon: FileText,
    requiredRole: 'admin',
    requiredLevel: 2,
    group: 'admin',
  },
  {
    id: 'feature_flags',
    href: '/flipper',
    label: 'Feature Flags',
    icon: Flag,
    requiredRole: 'admin',
    requiredLevel: 2,
    group: 'admin',
  },
  {
    id: 'sidekiq',
    href: '/sidekiq',
    label: 'Jobs',
    icon: LayoutDashboard,
    requiredRole: 'admin',
    requiredLevel: 2,
    group: 'admin',
  },
  {
    id: 'analytics',
    href: '/admin/analytics',
    label: 'Analytics',
    icon: BarChart3,
    requiredRole: 'admin',
    requiredLevel: 2,
    group: 'admin',
  },
];

/**
 * Filter navigation items by user role level
 * @param userRole - User's role
 * @param userLevel - User's role level
 * @returns Filtered navigation items
 */
export function getNavigationForRole(userRole: Role, userLevel: number): NavItem[] {
  return NAVIGATION_ITEMS.filter(item => item.requiredLevel <= userLevel);
}

/**
 * Get grouped navigation for sidebar
 * @param userRole - User's role
 * @param userLevel - User's role level
 * @returns Navigation grouped by level
 */
export function getGroupedNavigation(userRole: Role, userLevel: number) {
  // Filter items by role level
  const visibleItems = NAVIGATION_ITEMS.filter(item => item.requiredLevel <= userLevel);
  
  // Separate into flat items (user level) and grouped items (manager/admin)
  const flatItems = visibleItems.filter(item => item.requiredLevel === 0);
  const managerItems = visibleItems.filter(item => item.requiredLevel === 1);
  const adminItems = visibleItems.filter(item => item.requiredLevel === 2);
  
  return {
    flat: flatItems,                    // User items shown flat
    manager: managerItems,              // Manager items in collapsible
    admin: adminItems,                  // Admin items in collapsible
  };
}

/**
 * Check if user can access a specific navigation item
 * @param item - Navigation item
 * @param userLevel - User's role level
 * @returns boolean
 */
export function canAccessNavItem(item: NavItem, userLevel: number): boolean {
  return userLevel >= item.requiredLevel;
}
